import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, Layers, X } from 'lucide-react';
import TimesheetEditManager from '../../managers/TimesheetEditManager';
import {
  REVISION_TYPE_LABELS,
  TimesheetChangeLogAction,
  TimesheetChangeLogEntry,
  TimesheetRevisionSummary,
} from '../../interfaces/TimesheetEditInterface';
import { parseChangeLogImage, splitChangeLogImage } from './changeLogImage';
import { changeWhoLabel, fmtImageValue, fmtLogValue, fmtWhen, srcClass } from './changeLogFormat';
import './HistoryPanel.css';

/**
 * Payroll Timesheet Edit — change-history side panel (M2-B3; design v2 shot 12
 * + iteration-1 state 15 for versions/compare, unchanged in v2).
 *
 * Changes tab: the server change log grouped by editing sitting (editSessionID
 * when present; SYSTEM/EMPLOYEE/KIOSK/WEB rows group by consecutive source). Old→new
 * per field, time fields in the tenant's clock, minutes in hhmm, dollars as money.
 * Insert/Delete rows that carry a JSON row image (TAS-622) render it as a compact
 * field list — key fields first, the rest behind a disclosure.
 *
 * Versions tab: the revision list (no payloads). Pick two → Compare replaces
 * the panel body: both payloads are fetched and diffed CLIENT-SIDE with a
 * generic entity-grouped renderer (add / rem / chg rows) — historical payloads
 * are raw captured rows, so nothing here assumes today's column set.
 */

export interface HistoryPanelProps {
  open: boolean;
  employeeID: number;
  payPeriodID: number;
  employeeName: string;
  periodLabel: string;
  onClose: () => void;
}

// Value formatting (hhmm everywhere — closed contract) lives in changeLogFormat.ts,
// shared with the TAS-365 per-punch history modal.

// ── Changes tab grouping ─────────────────────────────────────────────────────

/** Who the session header names (rules in changeLogFormat.changeWhoLabel). */
export function changeLogWho(e: TimesheetChangeLogEntry, employeeName: string): string {
  return changeWhoLabel(e, employeeName);
}

export interface LogGroup {
  key: string;
  who: string;
  source: string;
  when: string;
  rows: TimesheetChangeLogEntry[];
}

/** Session-less rows (EMPLOYEE / KIOSK / WEB / SYSTEM writes) have no sitting id, so a
 *  quiet gap this long starts a new group — otherwise a week of mobile punches by
 *  the same worker would collapse into one card stamped with Monday's time. */
const SESSIONLESS_GAP_MS = 30 * 60 * 1000;

export function groupChangeLog(entries: TimesheetChangeLogEntry[], employeeName: string): LogGroup[] {
  const groups: LogGroup[] = [];
  let lastAt = NaN;
  entries.forEach((e) => {
    const key = e.editSessionID ?? `${e.source}:${e.adminEmployeeID ?? ''}`;
    const at = Date.parse(e.createdDate);
    const last = groups[groups.length - 1];
    const gapBreak = e.editSessionID == null
      && !Number.isNaN(at) && !Number.isNaN(lastAt) && at - lastAt > SESSIONLESS_GAP_MS;
    lastAt = at;
    if (last && last.key === key && !gapBreak) {
      last.rows.push(e);
      return;
    }
    groups.push({
      key,
      who: changeLogWho(e, employeeName),
      source: e.source,
      when: fmtWhen(e.createdDate),
      rows: [e],
    });
  });
  return groups.reverse(); // newest sitting first
}

// ── Client-side revision compare (generic renderer) ──────────────────────────

interface DiffRow { t: 'add' | 'rem' | 'chg'; label: string; field: string; old: string | null; new: string | null; }
interface DiffGroup { entity: string; rows: DiffRow[]; }

const COLLECTION_LABELS: Record<string, string> = {
  timeEntries: 'Piece punches',
  timeEntriesHourly: 'Hourly punches',
  employeeTasks: 'Task claims',
  miscEmployeeTasks: 'Misc tasks',
  employeeOperationalTasks: 'Operational tasks',
  employeeCustomerServiceWarranties: 'CSW claims',
  employeeSickClaims: 'Sick claims',
  driveTimeEntries: 'Drive time',
  employeeWorkOrderTasks: 'Work order claims',
  timesheetAnswers: 'Attestation answers',
};

/** Columns never diffed (mirrors the server's change-log diff exclusions). */
const DIFF_SKIP = new Set([
  'CreatedSource', 'ModifiedSource', 'CreatedByAdminEmployeeID', 'ModifiedByAdminEmployeeID',
]);

function rowID(row: Record<string, unknown>): string | null {
  const keys = Object.keys(row);
  const idKey = keys.find((k) => /ID$/.test(k) && typeof row[k] === 'number' && /^(?!.*(Employee|PayPeriod|Project|Account|Task|Timesheet)ID$).*/.test(k))
    ?? keys.find((k) => /ID$/.test(k) && typeof row[k] === 'number');
  return idKey != null ? `${idKey}:${row[idKey]}` : null;
}

function rowLabel(row: Record<string, unknown>, id: string | null): string {
  const dateKey = Object.keys(row).find((k) => /^(EntryDate|InDateTime|entryDate|inDateTime)$/.test(k));
  let datePart = '';
  if (dateKey && typeof row[dateKey] === 'string') {
    const t = Date.parse(row[dateKey] as string);
    if (!Number.isNaN(t)) {
      datePart = new Date(t).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
      });
    }
  }
  const idPart = id ? `#${id.split(':')[1]}` : '';
  return [datePart, idPart].filter(Boolean).join(' · ') || 'row';
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'string' && typeof b === 'string') {
    const ta = Date.parse(a);
    const tb = Date.parse(b);
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta === tb;
  }
  return String(a ?? '') === String(b ?? '');
}

function fmtDiffValue(field: string, v: unknown): string | null {
  if (v == null) return null;
  return fmtLogValue(field, String(v));
}

function diffCollection(entity: string, aRows: unknown[], bRows: unknown[]): DiffGroup | null {
  const rows: DiffRow[] = [];
  const aMap = new Map<string, Record<string, unknown>>();
  const bMap = new Map<string, Record<string, unknown>>();
  const aLoose: Record<string, unknown>[] = [];
  const bLoose: Record<string, unknown>[] = [];
  (aRows ?? []).forEach((r) => {
    const row = r as Record<string, unknown>;
    const id = rowID(row);
    if (id) aMap.set(id, row); else aLoose.push(row);
  });
  (bRows ?? []).forEach((r) => {
    const row = r as Record<string, unknown>;
    const id = rowID(row);
    if (id) bMap.set(id, row); else bLoose.push(row);
  });

  aMap.forEach((row, id) => {
    if (!bMap.has(id)) rows.push({ t: 'rem', label: rowLabel(row, id), field: 'Removed', old: null, new: null });
  });
  bMap.forEach((row, id) => {
    const prev = aMap.get(id);
    if (!prev) {
      rows.push({ t: 'add', label: rowLabel(row, id), field: 'Added', old: null, new: null });
      return;
    }
    const fields = new Set([...Object.keys(prev), ...Object.keys(row)]);
    fields.forEach((f) => {
      if (DIFF_SKIP.has(f)) return;
      const oldV = prev[f];
      const newV = row[f];
      if (typeof oldV === 'object' && oldV !== null) return; // nested objects: skip generically
      if (typeof newV === 'object' && newV !== null) return;
      if (!valuesEqual(oldV, newV)) {
        rows.push({ t: 'chg', label: rowLabel(row, id), field: f, old: fmtDiffValue(f, oldV), new: fmtDiffValue(f, newV) });
      }
    });
  });
  // ID-less rows (defensive against exotic historical captures): count delta only.
  if (aLoose.length !== bLoose.length) {
    rows.push({
      t: aLoose.length < bLoose.length ? 'add' : 'rem',
      label: `${Math.abs(aLoose.length - bLoose.length)} row(s)`,
      field: aLoose.length < bLoose.length ? 'Added' : 'Removed',
      old: null,
      new: null,
    });
  }
  return rows.length > 0 ? { entity, rows } : null;
}

export function diffRevisionPayloads(aPayload: unknown, bPayload: unknown): DiffGroup[] {
  const a = (aPayload ?? {}) as Record<string, unknown>;
  const b = (bPayload ?? {}) as Record<string, unknown>;
  const groups: DiffGroup[] = [];

  // Header first.
  const aHead = (a.header ?? {}) as Record<string, unknown>;
  const bHead = (b.header ?? {}) as Record<string, unknown>;
  const headRows: DiffRow[] = [];
  new Set([...Object.keys(aHead), ...Object.keys(bHead)]).forEach((f) => {
    if (!valuesEqual(aHead[f], bHead[f])) {
      headRows.push({ t: 'chg', label: 'Header', field: f, old: fmtDiffValue(f, aHead[f]), new: fmtDiffValue(f, bHead[f]) });
    }
  });
  if (headRows.length > 0) groups.push({ entity: 'Timesheet header', rows: headRows });

  // Known collections in a stable order, then anything array-shaped we don't know.
  const seen = new Set<string>(['header', 'schemaVersion', 'approvals']);
  Object.keys(COLLECTION_LABELS).forEach((key) => {
    seen.add(key);
    const g = diffCollection(
      COLLECTION_LABELS[key],
      (a[key] as unknown[]) ?? [],
      (b[key] as unknown[]) ?? []
    );
    if (g) groups.push(g);
  });
  // approvals: both halves, generically.
  const aAppr = (a.approvals ?? {}) as Record<string, unknown>;
  const bAppr = (b.approvals ?? {}) as Record<string, unknown>;
  new Set([...Object.keys(aAppr), ...Object.keys(bAppr)]).forEach((key) => {
    const g = diffCollection(
      `Approvals — ${key}`,
      (aAppr[key] as unknown[]) ?? [],
      (bAppr[key] as unknown[]) ?? []
    );
    if (g) groups.push(g);
  });
  // Unknown future collections — render by name without breaking.
  new Set([...Object.keys(a), ...Object.keys(b)]).forEach((key) => {
    if (seen.has(key)) return;
    const av = a[key];
    const bv = b[key];
    if (Array.isArray(av) || Array.isArray(bv)) {
      const g = diffCollection(key, (av as unknown[]) ?? [], (bv as unknown[]) ?? []);
      if (g) groups.push(g);
    }
  });
  return groups;
}

// ── Component ────────────────────────────────────────────────────────────────

const HistoryPanel: React.FC<HistoryPanelProps> = ({
  open, employeeID, payPeriodID, employeeName, periodLabel, onClose,
}) => {
  const [tab, setTab] = useState<'changes' | 'versions'>('changes');
  const [entries, setEntries] = useState<TimesheetChangeLogEntry[] | null>(null);
  const [revisions, setRevisions] = useState<TimesheetRevisionSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({ 0: true });
  const [cmp, setCmp] = useState<number[]>([]);
  const [compare, setCompare] = useState<DiffGroup[] | null>(null);
  const [compareMeta, setCompareMeta] = useState<{ a: TimesheetRevisionSummary; b: TimesheetRevisionSummary } | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // (Re)load on every open so the live session's own server-written rows appear.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoadError(null);
    setEntries(null);
    setRevisions(null);
    Promise.all([
      TimesheetEditManager.getChangeLog(employeeID, payPeriodID),
      TimesheetEditManager.getRevisions(employeeID, payPeriodID),
    ])
      .then(([log, revs]) => {
        if (!alive) return;
        setEntries(log.entries ?? []);
        setRevisions(revs.revisions ?? []);
        setOpenGroups({ 0: true });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const e = err as { response?: { status?: number; data?: { message?: string } } };
        setLoadError(
          e?.response?.status === 403
            ? 'You do not have permission to view timesheet history.'
            : e?.response?.data?.message ?? 'Could not load the change history.'
        );
        setEntries([]);
        setRevisions([]);
      });
    return () => { alive = false; };
  }, [open, employeeID, payPeriodID]);

  // Reset transient state when closed.
  useEffect(() => {
    if (!open) {
      setCompare(null);
      setCompareMeta(null);
      setCmp([]);
    }
  }, [open]);

  const onKeyDown = useCallback((ev: KeyboardEvent) => {
    if (ev.key !== 'Escape') return;
    if (compare) { setCompare(null); setCompareMeta(null); } else onClose();
  }, [compare, onClose]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onKeyDown]);

  const groups = useMemo(
    () => groupChangeLog(entries ?? [], employeeName),
    [entries, employeeName]
  );

  /** id → resolved admin name, mined from the change log (revision rows carry IDs only). */
  const adminNames = useMemo(() => {
    const map = new Map<number, string>();
    (entries ?? []).forEach((e) => {
      if (e.adminEmployeeID != null && e.adminEmployeeName) map.set(e.adminEmployeeID, e.adminEmployeeName);
    });
    return map;
  }, [entries]);

  const revsNewestFirst = useMemo(
    () => [...(revisions ?? [])].reverse(),
    [revisions]
  );

  const toggleCmp = (id: number) => {
    setCmp((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-2)));
  };

  const runCompare = async () => {
    if (cmp.length !== 2) return;
    const summaries = cmp
      .map((id) => (revisions ?? []).find((r) => r.timesheetRevisionSnapshotID === id))
      .filter(Boolean) as TimesheetRevisionSummary[];
    if (summaries.length !== 2) return;
    // Older revision on the left.
    summaries.sort((x, y) => x.timesheetRevisionSnapshotID - y.timesheetRevisionSnapshotID);
    setCompareLoading(true);
    try {
      const [a, b] = await Promise.all(
        summaries.map((s) => TimesheetEditManager.getRevisionByID(s.timesheetRevisionSnapshotID))
      );
      setCompare(diffRevisionPayloads(a.payload, b.payload));
      setCompareMeta({ a: summaries[0], b: summaries[1] });
    } catch {
      setLoadError('Could not load the revision payloads to compare.');
    } finally {
      setCompareLoading(false);
    }
  };

  if (!open) return null;

  const revTypeLabel = (t: number) => REVISION_TYPE_LABELS[t] ?? `Type ${t}`;
  const revWho = (r: TimesheetRevisionSummary) => {
    if (r.createdByAdminEmployeeID != null) {
      return adminNames.get(r.createdByAdminEmployeeID) ?? `Admin #${r.createdByAdminEmployeeID}`;
    }
    if (r.createdByEmployeeID != null) return employeeName || `Employee #${r.createdByEmployeeID}`;
    return '—';
  };

  const actx = (a: number) => (
    a === 1 ? <span className="pe-actx ins">Added</span>
      : a === 3 ? <span className="pe-actx del">Deleted</span>
        : <span className="pe-actx upd">Changed</span>
  );

  /** Insert/Delete row image → compact field list; null when the row has no
   *  parseable image (older payroll rows), so the caller keeps the plain line. */
  const imageBody = (r: TimesheetChangeLogEntry) => {
    if (r.fieldName != null) return null;
    const isIns = r.action === TimesheetChangeLogAction.Insert;
    const isDel = r.action === TimesheetChangeLogAction.Delete;
    if (!isIns && !isDel) return null;
    const image = parseChangeLogImage(isIns ? r.newValue : r.oldValue);
    if (!image) return null;
    const { key, rest } = splitChangeLogImage(image);
    if (key.length === 0 && rest.length === 0) return null;
    const row = (f: { label: string; field: string; value: string | null }) => (
      <React.Fragment key={f.field}>
        <dt>{f.label}</dt>
        <dd>{fmtImageValue(f.field, f.value)}</dd>
      </React.Fragment>
    );
    return (
      <div className={`pe-img ${isIns ? 'ins' : 'del'}`}>
        {key.length > 0 && <dl>{key.map(row)}</dl>}
        {rest.length > 0 && (
          <details>
            <summary>{rest.length} more field{rest.length === 1 ? '' : 's'}</summary>
            <dl className="rest">{rest.map(row)}</dl>
          </details>
        )}
      </div>
    );
  };

  const changesBody = (entries == null)
    ? <p className="pe-note">Loading change history…</p>
    : groups.length === 0
      ? <p className="pe-note">No changes recorded for this pay period.</p>
      : groups.map((g, i) => (
        <div key={`${g.key}-${i}`} className={`pe-sess${openGroups[i] ? '' : ' closed'}`}>
          <button
            className="pe-sesshead"
            onClick={() => setOpenGroups((prev) => ({ ...prev, [i]: !prev[i] }))}
            type="button"
          >
            <ChevronDown className="cv" />
            <span className="pe-sesswho">
              <span className="w">{g.who}</span>
              <span className="t">{g.when} · {g.rows.length} change{g.rows.length === 1 ? '' : 's'}</span>
            </span>
            <span className={`pe-src ${srcClass(g.source)}`}>{g.source || '?'}</span>
            {/* Revert slot — a later milestone drops the action here (design §3).
                Nothing is rendered until it works: a control that looks live and
                does nothing is worse than no control. */}
          </button>
          <div className="pe-sessbody">
            {g.rows.map((r) => (
              <div key={r.timesheetChangeLogID} className="pe-clog">
                <span className="act">{actx(r.action)}</span>
                <span>
                  <span className="ent">{r.entityType} #{r.entityID}</span>
                  {r.fieldName ? <> · <span className="fld">{r.fieldName}</span></> : null}
                </span>
                {imageBody(r) ?? (
                  <span className="diff">
                    {r.oldValue != null && (
                      <>
                        <span className="old">{fmtLogValue(r.fieldName, r.oldValue)}</span>
                        <ArrowRight className="ar" />
                      </>
                    )}
                    <span className="new">{fmtLogValue(r.fieldName, r.newValue)}</span>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ));

  const versionsBody = (revisions == null)
    ? <p className="pe-note">Loading revisions…</p>
    : revsNewestFirst.length === 0
      ? <p className="pe-note">No revisions recorded for this pay period — the first one is written at submit.</p>
      : (
        <>
          <p className="pe-note" style={{ margin: '0 0 11px' }}>
            Pick two revisions to compare. The diff is computed client-side from the two saved payloads.
          </p>
          {revsNewestFirst.map((r) => (
            <label
              key={r.timesheetRevisionSnapshotID}
              className={`pe-revrow${cmp.includes(r.timesheetRevisionSnapshotID) ? ' sel' : ''}`}
            >
              <input
                type="checkbox"
                checked={cmp.includes(r.timesheetRevisionSnapshotID)}
                onChange={() => toggleCmp(r.timesheetRevisionSnapshotID)}
              />
              <span className="rv">
                <span className="t">{revTypeLabel(r.revisionType)}</span>
                <span className="s">{fmtWhen(r.createdDate)} · {revWho(r)}</span>
              </span>
              <span className={`pe-src ${r.editSessionID ? 'payroll' : 'system'}`}>#{r.timesheetRevisionSnapshotID}</span>
            </label>
          ))}
          <div className="pe-cmpbar">
            <span className="pe-note grow">{cmp.length}/2 selected</span>
            <button
              className="pe-btn primary"
              onClick={runCompare}
              disabled={cmp.length !== 2 || compareLoading}
              type="button"
            >
              <Layers /> {compareLoading ? 'Loading…' : 'Compare'}
            </button>
          </div>
        </>
      );

  const compareBody = compare != null && compareMeta != null && (
    <>
      <div className="pe-ban info" style={{ marginBottom: 14 }}>
        <Layers />
        <div className="grow">
          <b>{revTypeLabel(compareMeta.a.revisionType)} → {revTypeLabel(compareMeta.b.revisionType)}</b>
          {fmtWhen(compareMeta.a.createdDate)} compared with {fmtWhen(compareMeta.b.createdDate)}.
          Diff is computed client-side from the two saved payloads.
        </div>
      </div>
      {compare.length === 0 && <p className="pe-note">These two revisions are identical in every compared field.</p>}
      {compare.map((g) => (
        <div key={g.entity} className="pe-diffgrp">
          <h4>{g.entity}</h4>
          {g.rows.map((r, i) => (
            <div key={`${g.entity}-${i}`} className={`pe-diffrow ${r.t}`}>
              <span className="grow">
                <b>{r.label}</b>
                <br />
                <span className="pe-note">{r.field}</span>
              </span>
              <span className="mono">
                {r.old != null && <span className="old">{r.old} </span>}
                {r.new != null ? r.new : r.t === 'rem' ? <span className="gone">removed</span> : ''}
              </span>
            </div>
          ))}
        </div>
      ))}
    </>
  );

  return (
    <>
      <div className="pe-scrim open" onClick={onClose} aria-hidden="true" />
      <aside className="pe-panel open" aria-label="Change history">
        <div className="pe-phead">
          <div className="grow">
            <h3>{compare ? 'Compare revisions' : 'Change history'}</h3>
            <div className="sub">{employeeName} · {periodLabel}</div>
          </div>
          {compare && (
            <button className="pe-btn" onClick={() => { setCompare(null); setCompareMeta(null); }} type="button">
              <ArrowLeft /> Back
            </button>
          )}
          <button className="pe-iact" onClick={onClose} title="Close" type="button"><X /></button>
        </div>
        {!compare && (
          <div className="pe-ptabs">
            <button className={tab === 'changes' ? 'on' : ''} onClick={() => setTab('changes')} type="button">Changes</button>
            <button className={tab === 'versions' ? 'on' : ''} onClick={() => setTab('versions')} type="button">Versions</button>
          </div>
        )}
        <div className="pe-pbody">
          {loadError && <p className="pe-note" style={{ marginBottom: 10 }}>{loadError}</p>}
          {compare ? compareBody : tab === 'changes' ? changesBody : versionsBody}
        </div>
      </aside>
    </>
  );
};

export default HistoryPanel;
